import {
  Box,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  Circle,
  VisuallyHidden,
  Badge,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { Link as RouterLink } from 'react-router-dom';
import ApplePassFront from './previews/apple/ApplePassFront';
import TemplateCardPoster, { hasCardPoster } from './TemplateCardPoster';
import { ScaledPassPreview } from './previews/ScaledPassPreview';
import { CheckIcon, WarningIcon } from '@chakra-ui/icons';
import { useContext } from 'react';
import { MeContext } from '../../../context/MeContext';
import { Entity, MeContextState } from '../../../store/types/account';
import { resolveNfcCapabilities } from '../../../utils/resolveNfcCapabilities';
import TemplateCardMenu from './TemplateCardMenu';
import PublishTemplateButton from './PublishTemplateButton';
import { EnhancedDesignType } from '../../../store/types/template';
import { APPLE_ENHANCED_SECTION_IDS } from './utils/appleEnhancedDesignConstants';
import { DEVICE_TAB_PARAM, SECTION_PARAM } from './utils/sectionFocus';

const dotPulse = keyframes`
  0%, 60%, 100% { transform: scale(0.65); opacity: .35; }
  30% { transform: scale(1); opacity: 1; }
`;

const PassTemplatesListItem = ({ previewData }: { previewData: any }) => {
  const { state: meStateRaw } = useContext(MeContext) as {
    state: MeContextState;
  };
  const meState = meStateRaw;
  const localisationEnabled = meState?.localisationEnabled;
  const isDraft = previewData.status === 'draft';
  const isPublished = previewData.status === 'published';
  // A draft that relies on a premium feature the plan doesn't include can't be
  // published until the plan is upgraded.
  const featureNotInPlan =
    isDraft &&
    ((!localisationEnabled && !!previewData.hasLocalisationFields) ||
      (!meState?.passBindingEnabled && !!previewData.hasEnhancedEventTicket));

  const enhancedDesignType: EnhancedDesignType | undefined =
    previewData.appleEnhancedDesign?.type;
  const enhancedSectionId =
    meState?.appleEnhancedDesignEnabled && enhancedDesignType
      ? APPLE_ENHANCED_SECTION_IDS[enhancedDesignType]
      : undefined;

  const owner = (
    meState?.entities as (Entity & { id?: string | number })[] | undefined
  )?.find((entity) => String(entity.id) === String(previewData.entityId));
  const { appleNfcAvailable } = resolveNfcCapabilities(owner);

  const templateName = previewData.templateName ?? '';
  const showPoster =
    !!meState?.appleEnhancedDesignEnabled && hasCardPoster(previewData);

  const previewBlock =
    previewData.processingStatus === 'processing' ? (
      <Box
        position="static"
        inset="18% 10% auto 9%"
        minH="350px"
        bg="rgba(41, 50, 58, 0.6)"
        color="white"
        borderRadius="10px"
        border="1px solid #E0E0E6"
        py={2}
      >
        <Box
          display={previewData.passType === 'eventTicket' ? 'block' : 'none'}
          w="100px"
          h="20px"
          pos="absolute"
          left="50%"
          transform="translateX(-50%)"
          top="0"
          pointerEvents="none"
          overflow="hidden"
        >
          <Circle
            display={previewData.passType === 'eventTicket' ? 'block' : 'none'}
            bg="#F8F8F8"
            size="100px"
            pos="absolute"
            bottom="0"
            pointerEvents="none"
            border="1px solid #E0E0E6"
          />
        </Box>
        <Flex
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap={2}
          h="300px"
        >
          <Text fontSize="18px" textStyle="headingFamilyMedium">
            Building template
          </Text>
          <Text fontSize="14px" textStyle="bodyFamilyRegular">
            This may take a few moments
          </Text>
          <HStack spacing="8px" mt={4} aria-live="polite" role="status">
            <VisuallyHidden>Loading</VisuallyHidden>
            {[0, 1, 2].map((i) => (
              <Circle
                key={i}
                size="10px"
                bg="white"
                animation={`${dotPulse} 1.2s ease-in-out ${i * 0.18}s infinite`}
              />
            ))}
          </HStack>
        </Flex>
      </Box>
    ) : (
      <Box pos="relative" opacity={isDraft ? 0.6 : undefined}>
        <Box
          as={RouterLink}
          to={previewData.editUrl}
          display="block"
          aria-label={`View or edit ${templateName}`}
        >
          {showPoster ? (
            <TemplateCardPoster previewData={previewData} />
          ) : (
            <ApplePassFront
              previewData={{
                ...previewData,
                nfc: {
                  ...previewData.nfc,
                  enabled: !!previewData.nfc?.enabled && appleNfcAvailable,
                },
                hideIdPlaceholder: true,
              }}
              templatesPage
              notifOpen={false}
              cutoutBg="white"
            />
          )}
        </Box>
        {previewData.processingStatus === 'updating' && (
          <Flex
            pos="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(41, 50, 58, 0.8)"
            borderRadius="10px"
            color="white"
            alignItems="center"
            justifyContent="center"
            opacity="1"
            zIndex="1"
          >
            <Flex alignItems="center" flexDirection="column" gap={2}>
              <Spinner />
              <Text
                fontSize="14px"
                textStyle="bodyRoman"
                textAlign="center"
                cursor="default"
              >
                Syncing passes...
                <br />
                Refresh page for updates
              </Text>
            </Flex>
          </Flex>
        )}
      </Box>
    );

  return (
    <Box
      bg="white"
      borderRadius="16px"
      px={8}
      py={6}
      h="full"
      display="flex"
      flexDirection="column"
      layerStyle="hoverCard"
    >
      <Flex justifyContent="flex-end" mb={3} minH="22px">
        {isPublished && (
          <Badge
            display="flex"
            alignItems="center"
            gap={1}
            bg="rgba(51, 153, 0, 0.1)"
            color="#339900"
            border="1px solid"
            borderColor="#339900"
            borderRadius="full"
            px={3}
            py={1.5}
            textTransform="none"
          >
            <CheckIcon boxSize="14px" />
            <Text fontSize="14px" fontWeight="500">
              Published
            </Text>
          </Badge>
        )}
        {isDraft && (
          <Badge
            bg="rgba(201, 201, 201, 0.5)"
            color="#6C6C6C"
            borderRadius="md"
            px={3}
            py={1.5}
          >
            <Text fontSize="14px" fontWeight="500">
              DRAFT
            </Text>
          </Badge>
        )}
      </Flex>

      <ScaledPassPreview>{previewBlock}</ScaledPassPreview>

      <HStack
        justifyContent="space-between"
        alignItems="center"
        mt={4}
        minW={0}
      >
        <Heading
          fontSize="18px"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          minW={0}
        >
          {templateName}
        </Heading>
        <TemplateCardMenu previewData={previewData} />
      </HStack>

      <HStack mt={2} spacing={2} flexWrap="wrap">
        {previewData.dynamicForm && !isDraft && (
          <Badge
            as={RouterLink}
            to={`${previewData.editUrl}?tab=form`}
            bg="#E5E6E8"
            color="#434850"
            border="1px solid #434850"
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
            textDecoration="none"
            px={3}
            py={1.5}
          >
            <Text fontSize="12px" textStyle="bodyRoman">
              Form
            </Text>
          </Badge>
        )}
        {enhancedSectionId && (
          <Badge
            as={RouterLink}
            to={`${previewData.editUrl}?${SECTION_PARAM}=${enhancedSectionId}&${DEVICE_TAB_PARAM}=apple`}
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="22px"
            px="8px"
            py="4px"
            bg="rgba(98, 69, 173, 0.1)"
            color="#6245ad"
            border="1px solid #6245ad"
            borderRadius="full"
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
            textDecoration="none"
            textTransform="none"
          >
            <Text fontSize="12px" fontWeight="400" lineHeight="1.3">
              Apple enhanced
            </Text>
          </Badge>
        )}
        {featureNotInPlan && (
          <Badge
            display="flex"
            alignItems="center"
            gap={1}
            bg="warningOrangeLight"
            color="warningOrange"
            border="1px solid"
            borderColor="warningOrange"
            borderRadius="full"
            px={3}
            py={1.5}
            textTransform="none"
          >
            <WarningIcon boxSize="11px" />
            <Text fontSize="12px" fontWeight="500">
              Feature not in plan
            </Text>
          </Badge>
        )}
      </HStack>

      {isDraft && (
        <Box mt="auto" pt={4}>
          <PublishTemplateButton previewData={previewData} />
        </Box>
      )}
    </Box>
  );
};

export default PassTemplatesListItem;
